import { createSlice } from '@reduxjs/toolkit';
import { fetchPutawayPending, fetchPutawayRow, scanPutawayCode, storePutaway } from './operatorPutawayThunks';
import { putawayRowChosen } from './operatorPutawayActions';
import { PUTAWAY_SOURCE, PUTAWAY_STEP, putawayRowOf } from './operatorPutawayUtils';
import { describeCandidates } from '../operator/operatorCandidates';
import { OPERATOR_ERROR, isRetryable } from '../operator/operatorErrors';
import { acceptedCode } from '../operator/operatorScanGate';
import { operatorWarehouseSelected } from '../operator/operatorSlice';
import { isBackgroundRequest } from '../feedback/feedbackSlice';

/*
 * Storing on the floor: the pending rows of one tab, the row being stored and
 * where it goes.
 */
const initialList = {
  rows: [],
  count: 0,
  counts: null,
  page: 1,
  hasMore: false,
  loading: false,
  error: null,
  requestId: null,
  /** When its first page last came, by the phone's clock. */
  receivedAt: null,
};

const initialTask = {
  selectedKey: null,
  /** `{code, name, suggested}` once the operator scanned where it goes. */
  destination: null,
  step: PUTAWAY_STEP.PRODUCT,
  scan: { lastCode: null },
  /** A slot code being asked to the scan. */
  scanning: false,
  pending: null,
  resolution: null,
  lastWrite: null,
  /**
   * The row a task of the tray opened, `{key, row, loading, error, requestId}`.
   * It stays chosen even when the list's page does not bring it.
   */
  requested: null,
};

const initialState = { source: PUTAWAY_SOURCE.RECEIVING, list: initialList, ...initialTask };

const intentOf = (arg) => {
  const { idempotencyKey, ...rest } = arg || {};
  return rest;
};

const operatorPutawaySlice = createSlice({
  name: 'operatorPutaway',
  initialState,
  reducers: {
    putawayEntered: (state) => {
      state.resolution = null;
    },
    putawayLeft: (state) => {
      state.resolution = null;
    },
    /** @param {{payload: 'receiving'|'restock'}} action */
    putawaySourceChanged: (state, action) => {
      if (state.source === action.payload) return;
      Object.assign(state, initialTask, { source: action.payload, list: initialList });
    },
    putawayQuantityRequested: (state) => {
      if (state.destination) state.step = PUTAWAY_STEP.QUANTITY;
    },
    putawayQuantityCancelled: (state) => {
      state.step = PUTAWAY_STEP.DESTINATION;
    },
    putawaySelectionCancelled: (state) => {
      Object.assign(state, initialTask, { scan: state.scan });
    },
    putawayResolutionDismissed: (state) => {
      state.resolution = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(operatorWarehouseSelected, () => initialState)

      .addCase(fetchPutawayPending.pending, (state, action) => {
        state.list.requestId = action.meta.requestId;
        if (isBackgroundRequest(action)) return;
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchPutawayPending.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.list.requestId) return;
        const page = action.meta.arg?.page ?? 1;
        const results = action.payload.results || [];
        state.list.loading = false;
        state.list.error = null;
        state.list.page = page;
        state.list.rows = page === 1 ? results : [...state.list.rows, ...results];
        state.list.count = action.payload.count ?? state.list.rows.length;
        state.list.counts = action.payload.counts || null;
        state.list.hasMore = Boolean(action.payload.next);
        if (page === 1) state.list.receivedAt = action.meta.receivedAt;
        // A row that is gone — stored by someone else — cannot stay selected. The one a task opened
        // may just be on another page. A store still out is the one that emptied it, reloading: it
        // settles itself, and until then the screen takes no read.
        if (state.selectedKey && !putawayRowOf(state)) {
          Object.assign(state, initialTask, { scan: state.scan, pending: state.pending, lastWrite: state.lastWrite });
        }
      })
      .addCase(fetchPutawayPending.rejected, (state, action) => {
        // A refresh in the background fails in silence: the list stays as it was, and the next one tries again.
        if (action.meta.requestId !== state.list.requestId || isBackgroundRequest(action)) return;
        state.list.loading = false;
        state.list.error = action.payload ?? { kind: OPERATOR_ERROR.REFUSED, message: action.error.message };
      })

      // A task of the tray: its tab, and its row chosen once it comes.
      .addCase(fetchPutawayRow.pending, (state, action) => {
        const { source, key } = action.meta.arg;
        Object.assign(state, initialTask, {
          source,
          list: state.source === source ? state.list : initialList,
          scan: state.scan,
          requested: { key, row: null, loading: true, error: null, requestId: action.meta.requestId },
        });
      })
      .addCase(fetchPutawayRow.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requested?.requestId) return;
        state.requested.loading = false;
        state.requested.row = action.payload;
        if (!action.payload) return;
        state.selectedKey = action.payload.key;
        state.step = PUTAWAY_STEP.DESTINATION;
      })
      .addCase(fetchPutawayRow.rejected, (state, action) => {
        if (action.meta.requestId !== state.requested?.requestId) return;
        state.requested.loading = false;
        state.requested.error = action.payload ?? { kind: OPERATOR_ERROR.REFUSED, message: action.error.message };
      })

      .addCase(scanPutawayCode.pending, (state) => {
        state.scanning = true;
      })
      .addCase(scanPutawayCode.fulfilled, (state, action) => {
        state.scanning = false;
        state.scan.lastCode = acceptedCode(action);
        const result = action.payload;
        if (result.kind === 'row') {
          state.selectedKey = result.key;
          state.step = PUTAWAY_STEP.DESTINATION;
        } else if (result.kind === 'destination') {
          state.destination = result.destination;
        } else {
          state.resolution = {
            kind: 'ambiguous',
            origin: 'local',
            message: 'Hay más de una fila de ese producto: elige cuál.',
            options: result.options,
          };
        }
      })
      .addCase(scanPutawayCode.rejected, (state, action) => {
        if (!action.meta.condition) state.scanning = false;
      })
      .addCase(putawayRowChosen, (state, action) => {
        state.selectedKey = action.payload;
        state.destination = null;
        state.step = PUTAWAY_STEP.DESTINATION;
        state.resolution = null;
      })

      .addCase(storePutaway.pending, (state, action) => {
        state.pending = 'store';
        state.resolution = null;
        state.lastWrite = { type: storePutaway.typePrefix, arg: intentOf(action.meta.arg), key: action.meta.requestId };
      })
      .addCase(storePutaway.fulfilled, (state) => {
        Object.assign(state, initialTask, { scan: state.scan });
      })
      .addCase(storePutaway.rejected, (state, action) => {
        if (action.meta.condition) return;
        state.pending = null;
        const error = action.payload;
        if (error?.kind === OPERATOR_ERROR.AMBIGUOUS) {
          state.resolution = {
            kind: 'ambiguous',
            origin: 'server',
            message: error.message,
            ...describeCandidates(error.candidates),
            arg: intentOf(action.meta.arg),
            key: action.meta.requestId,
          };
        }
        if (!isRetryable(error)) state.lastWrite = null;
      });
  },
});

export const {
  putawayEntered,
  putawayLeft,
  putawaySourceChanged,
  putawayQuantityRequested,
  putawayQuantityCancelled,
  putawaySelectionCancelled,
  putawayResolutionDismissed,
} = operatorPutawaySlice.actions;

export const selectPutawayRow = (state) => putawayRowOf(state.operatorPutaway);

/** Whether a read code is welcome right now. */
export const selectPutawayAccepting = (state) => {
  const putaway = state.operatorPutaway;
  return (
    !putaway.pending &&
    !putaway.resolution &&
    !putaway.scanning &&
    !putaway.requested?.loading &&
    putaway.step !== PUTAWAY_STEP.QUANTITY
  );
};

export default operatorPutawaySlice.reducer;
